import { track } from '../services/analytics';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useAuth } from './AuthContext';
import { isNative, platform } from '../services/platform';
import { PRO_ENTITLEMENT_ID, REVENUECAT_KEYS, WEB_BILLING_ON, WEB_PLANS, IOS_STORE_BILLING_ON } from '../config/entitlements';
import { supabase } from '../services/api';
import { isProSubscription, plansFor } from '../utils/subscription';

const EntitlementContext = createContext(null);

const apiKey = REVENUECAT_KEYS[platform] || '';
// Where Pro is sold in this build: 'store' in the native apps (RevenueCat),
// 'web' on the website when Stripe checkout is switched on, or nowhere. Where
// it is sold nowhere, nothing is locked, so a misconfigured build can never
// strand users behind a paywall they cannot pay.
const STORE_BILLING = isNative && !!apiKey && (platform !== 'ios' || IOS_STORE_BILLING_ON);
const WEB_BILLING = !isNative && WEB_BILLING_ON && !!supabase;
const BILLING_ENABLED = STORE_BILLING || WEB_BILLING;
const CHANNEL = STORE_BILLING ? 'store' : WEB_BILLING ? 'web' : null;

// Fired to ask the layout for the sign-in window.
export const OPEN_SIGN_IN = 'ultronic:open-sign-in';

// The offer someone was looking at when they left to create an account or sign
// in, so they land back on it and not on the locked page. Kept for the tab
// only, and only briefly; it survives the round trip to Apple or Google.
const RESUME_KEY = 'ultronic_resume_paywall';
const RESUME_MS = 15 * 60 * 1000;
export function rememberPaywall(feature) {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify({ feature, at: Date.now() }));
  } catch {
    // Storage unavailable: they reopen the offer themselves.
  }
}
export function takeRememberedPaywall() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(RESUME_KEY) || 'null');
    sessionStorage.removeItem(RESUME_KEY);
    return saved && typeof saved.feature === 'string' && Date.now() - saved.at < RESUME_MS ? saved.feature : null;
  } catch {
    return null;
  }
}

const callFunction = async (name, body) => {
  const { data, error } = await supabase.functions.invoke(name, { method: 'POST', body });
  if (error) {
    // The function's own message, when it sent one.
    let message = '';
    try { message = (await error.context?.json())?.error || ''; } catch { /* not JSON */ }
    throw new Error(message || 'Request failed');
  }
  return data;
};

// "?checkout=success" is on the address when the person returns from paying.
const checkoutReturn = () => {
  try {
    return new URLSearchParams(window.location.search).get('checkout');
  } catch {
    return null;
  }
};
const clearCheckoutReturn = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('checkout');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch {
    // Leaving it in the address is harmless.
  }
};

// Last confirmed answer from the store. A subscriber who opens the app offline,
// or while the billing service is failing, keeps what they paid for.
const CACHE_KEY = 'ultronic_pro_confirmed';
const readCachedPro = () => {
  try {
    return localStorage.getItem(CACHE_KEY) === '1';
  } catch {
    return false;
  }
};
const writeCachedPro = (value) => {
  try {
    localStorage.setItem(CACHE_KEY, value ? '1' : '0');
  } catch {
    // Storage unavailable: the next successful check will answer again.
  }
};

const hasPro = (customerInfo) => !!customerInfo?.entitlements?.active?.[PRO_ENTITLEMENT_ID];

export function EntitlementProvider({ children }) {
  const { user } = useAuth();
  const [isPro, setIsPro] = useState(() => !BILLING_ENABLED || readCachedPro());
  // 'checking' | 'ready' | 'unavailable'
  const [status, setStatus] = useState(BILLING_ENABLED ? 'checking' : 'ready');
  const [packages, setPackages] = useState([]);
  const [paywallFeature, setPaywallFeature] = useState(null);
  // The website's own subscription record, for the Free and Pro page.
  const [subscription, setSubscription] = useState(null);
  // '' | 'confirming' | 'confirmed' | 'slow' | 'cancelled'
  const [checkoutState, setCheckoutState] = useState(() => (WEB_BILLING && checkoutReturn() === 'success' ? 'confirming' : WEB_BILLING && checkoutReturn() === 'cancelled' ? 'cancelled' : ''));
  const configured = useRef(false);
  const userId = user?.id || null;

  // Website: Pro is whatever the account's subscription row says.
  const refreshWeb = useCallback(async () => {
    if (!userId) {
      setSubscription(null);
      setIsPro(false);
      writeCachedPro(false);
      setStatus('ready');
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, plan, current_period_end, cancel_at_period_end')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      const pro = isProSubscription(data);
      setSubscription(data || null);
      setIsPro(pro);
      writeCachedPro(pro);
      setStatus('ready');
      return pro;
    } catch (err) {
      // No definite answer: keep what was last confirmed.
      console.warn('Subscription check failed:', err?.message || err);
      setStatus('unavailable');
      return null;
    }
  }, [userId]);

  // Only a definite answer from the store changes what is unlocked.
  const confirm = useCallback((customerInfo) => {
    const pro = hasPro(customerInfo);
    setIsPro(pro);
    writeCachedPro(pro);
    return pro;
  }, []);

  const refresh = useCallback(async () => {
    if (!BILLING_ENABLED) return;
    if (WEB_BILLING) {
      await refreshWeb();
      return;
    }
    try {
      if (!configured.current) {
        await Purchases.configure({ apiKey });
        await Purchases.addCustomerInfoUpdateListener(confirm);
        configured.current = true;
      }
      // Tie purchases to the signed-in account so they follow the user across devices.
      if (userId) {
        const { customerInfo } = await Purchases.logIn({ appUserID: userId });
        confirm(customerInfo);
      } else {
        const { isAnonymous } = await Purchases.isAnonymous();
        const { customerInfo } = isAnonymous ? await Purchases.getCustomerInfo() : await Purchases.logOut();
        confirm(customerInfo);
      }
      setStatus('ready');
    } catch (err) {
      console.warn('Billing check failed:', err?.message || err);
      setStatus('unavailable');
    }
    try {
      const offerings = await Purchases.getOfferings();
      setPackages(offerings?.current?.availablePackages || []);
    } catch (err) {
      console.warn('Could not load subscription options:', err?.message || err);
    }
  }, [userId, confirm, refreshWeb]);

  useEffect(() => { refresh(); }, [refresh]);

  // Back from checkout: the payment notice reaches us a moment after the
  // person does, so ask a few times before saying it is taking a while.
  useEffect(() => {
    if (checkoutState !== 'confirming' || !userId) return undefined;
    let cancelled = false;
    let tries = 0;
    const check = async () => {
      if (cancelled) return;
      const pro = await refreshWeb();
      if (cancelled) return;
      if (pro) {
        setCheckoutState('confirmed');
        clearCheckoutReturn();
        track('purchase_confirmed');
      } else if (++tries >= 10) {
        setCheckoutState('slow');
        clearCheckoutReturn();
      } else {
        setTimeout(check, 2000);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [checkoutState, userId, refreshWeb]);

  useEffect(() => {
    if (checkoutState === 'cancelled') clearCheckoutReturn();
  }, [checkoutState]);

  // A check that failed at launch is retried whenever the app comes back.
  useEffect(() => {
    if (!BILLING_ENABLED) return undefined;
    const onVisible = () => {
      // The website also re-checks on return, so a change made on Stripe's
      // billing page, or on another device, shows up.
      if (document.visibilityState === 'visible' && (status !== 'ready' || WEB_BILLING)) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [status, refresh]);

  const purchase = useCallback(async (aPackage) => {
    if (WEB_BILLING) {
      if (!userId) return { ok: false, needsSignIn: true, message: 'Create a free account or sign in first, so your purchase is tied to you and works on every device.' };
      try {
        track('checkout_started', { plan: aPackage.id });
        const { url } = await callFunction('create-checkout', { plan: aPackage.id });
        if (!url) throw new Error('No checkout address');
        window.location.assign(url);
        // The page is leaving for Stripe; nothing more happens here.
        return { ok: false, cancelled: true };
      } catch (err) {
        return { ok: false, message: err?.message && err.message !== 'Request failed' ? err.message : 'Checkout could not be started. Check your connection and try again.' };
      }
    }
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage });
      if (confirm(customerInfo)) return { ok: true };
      // The store accepted the request but has not granted access yet
      // (for example a purchase awaiting approval).
      return { ok: false, message: 'Your purchase is being processed. Pro will unlock as soon as the store confirms it. If it does not, tap Restore Purchases.' };
    } catch (err) {
      if (err?.userCancelled) return { ok: false, cancelled: true };
      return { ok: false, message: 'The purchase did not complete. If you were charged, tap Restore Purchases and Pro will unlock.' };
    }
  }, [confirm, userId]);

  const restore = useCallback(async () => {
    if (WEB_BILLING) {
      if (!userId) return { ok: false, needsSignIn: true, message: 'Sign in to the account you bought Pro with.' };
      const pro = await refreshWeb();
      return { ok: !!pro, message: pro ? '' : pro === null ? 'Your subscription could not be checked. Check your connection and try again.' : 'No active subscription was found for this account.' };
    }
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      const pro = confirm(customerInfo);
      return { ok: pro, message: pro ? '' : 'No active subscription was found for this store account.' };
    } catch {
      return { ok: false, message: 'Purchases could not be restored. Check your connection and try again.' };
    }
  }, [confirm, userId, refreshWeb]);

  // Website only: Stripe's page for changing the card, invoices and cancelling.
  const manage = useCallback(async () => {
    try {
      const { url } = await callFunction('billing-portal');
      if (!url) throw new Error('No address');
      window.location.assign(url);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err?.message && err.message !== 'Request failed' ? err.message : 'The billing page could not be opened. Please try again.' };
    }
  }, []);

  const openPaywall = useCallback((feature) => {
    track('paywall_shown', { feature: typeof feature === 'string' ? feature : 'general' });
    // Opened from nowhere in particular (the Free and Pro page): the offer as a whole.
    setPaywallFeature(typeof feature === 'string' && feature ? feature : 'general');
    // Options that failed to load earlier get another chance each time.
    if (BILLING_ENABLED) refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    isPro,
    ready: status !== 'checking',
    status,
    billingEnabled: BILLING_ENABLED,
    // 'store' | 'web' | null
    channel: CHANNEL,
    webPlans: WEB_BILLING ? plansFor(WEB_PLANS, subscription) : [],
    subscription,
    checkoutState,
    dismissCheckoutState: () => setCheckoutState(''),
    signedIn: !!userId,
    manage,
    packages,
    purchase,
    restore,
    refresh,
    paywallFeature,
    openPaywall,
    closePaywall: () => setPaywallFeature(null),
  }), [isPro, status, packages, purchase, restore, refresh, paywallFeature, openPaywall, subscription, checkoutState, userId, manage]);

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlement() {
  const ctx = useContext(EntitlementContext);
  if (!ctx) throw new Error('useEntitlement must be used within EntitlementProvider');
  return ctx;
}
